import { Popup } from 'mapbox-gl'
import { useEffect, useRef, useState } from 'react'

import PolygonPopupContent from './PolygonPopupContent'
import { useMapStore, usePolygonStore } from '../../../../../state-library/src'
import { IPolygonProperties } from '../../../../../types-library/src'
import { presenter } from '@wiggot/core-apis/src/presenter'

interface Props {
  layer: string
  level: 'state' | 'municipality' | 'neighborhood' | 'block' | 'property'
}

const PolygonPopup = ({ layer, level }: Props): JSX.Element => {
  const { map, hoveredLayer, drawingTool } = useMapStore((state) => state)

  const { isEnabledHeatMap } = usePolygonStore()

  const isEnabledHeatMapRef = useRef(isEnabledHeatMap)
  isEnabledHeatMapRef.current = isEnabledHeatMap

  const hoveredLayerRef = useRef(hoveredLayer)
  hoveredLayerRef.current = hoveredLayer

  const drawingToolRef = useRef(drawingTool)
  drawingToolRef.current = drawingTool

  const htmlRef = useRef<HTMLDivElement | null>(null)
  const popupRef = useRef<Popup | null>(null)

  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [polygonData, setPolygonData] = useState<IPolygonProperties | null>(
    null
  )

  const polygonDataRef = useRef<IPolygonProperties | null>(null)
  polygonDataRef.current = polygonData

  const createPopup = (event: any) => {
    if (!map || hoveredLayerRef.current?.layerId) {
      return
    }

    if (
      drawingToolRef.current.isActive &&
      drawingToolRef.current.status !== 'processed'
    ) {
      return
    }

    if (!event?.features?.length) {
      return
    }

    const instance = new Popup({
      closeButton: false,
      closeOnMove: false,
      anchor: 'bottom',
    })
      ?.setLngLat(event.lngLat)
      ?.setDOMContent(htmlRef.current as any)
      ?.setOffset([-50, -50])
      ?.addTo(map)

    popupRef.current = instance

    const data = event?.features[0]?.properties

    const nomenclature = presenter<IPolygonProperties>({
      value: { ...data, centroid: JSON.parse(data?.centroid) },
      style: 'camel',
    })

    setPolygonData(nomenclature)
    setIsPopupOpen(true)
  }

  const removePopup = () => {
    if (!map || !popupRef?.current) {
      return
    }

    setIsPopupOpen(false)
    popupRef?.current?.remove()
    popupRef.current = null
    setPolygonData(null)
    polygonDataRef.current = null
  }

  useEffect(() => {
    const onMouseEnter = (event: any) => {
      if (!map || !isEnabledHeatMapRef?.current) {
        return
      }
      createPopup(event)
    }

    const onMouseLeave = (event: any) => {
      if (!map) {
        return
      }

      const isMouseOverPopup = htmlRef.current?.contains(
        event.originalEvent.toElement
      )

      if (!isMouseOverPopup) {
        removePopup()
      }
    }

    const onMouseMove = (event: any) => {
      if (!map || !isEnabledHeatMapRef?.current) {
        return
      }

      const data = event?.features[0]?.properties

      if (
        popupRef.current &&
        polygonDataRef?.current &&
        polygonDataRef?.current?.id !== data.id
      ) {
        removePopup()
        return
      }

      if (!popupRef.current) {
        createPopup(event)
        return
      }

      const lngLat = event.lngLat
      popupRef.current.setLngLat(lngLat)
    }

    if (map) {
      map.on('mouseenter', layer, onMouseEnter)
      map.on('mouseleave', layer, onMouseLeave)
      map.on('mousemove', layer, onMouseMove)
    }

    return () => {
      if (!map) {
        return
      }
      map.off('mouseenter', onMouseEnter)
      map.off('mouseleave', onMouseLeave)
      map.off('mousemove', onMouseMove)
    }
  }, [map, layer])

  useEffect(() => {
    if (hoveredLayer?.layerId && popupRef?.current) {
      removePopup()
    }
  }, [hoveredLayer])

  return (
    <div ref={htmlRef}>
      {isPopupOpen && polygonData ? (
        <PolygonPopupContent
          onMouseLeave={removePopup}
          polygon={polygonData}
          level={level}
        />
      ) : null}
    </div>
  )
}

export default PolygonPopup
